import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { toPascalCaseParams } from '@/shared/lib/to-pascal-case-params';
import { env } from '@/core/config/env';
import type {
  ConvertToTaskRequest,
  ConvertToTaskResponse,
  InterpretNoteAsTaskRequest,
  Note,
  NoteExportFormat,
  NoteFilters,
  NotePagedResult,
  NoteTaskProposal,
  NoteVersion,
  NoteVersionSummaryPagedResult,
} from '../types';
import type { NoteFormValues } from '../schemas';
import { notesMock, noteVersionsMock, noteVersionSummariesMock } from './notes.mock';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

// Notes are user-scoped, never tenant-scoped — every real call opts out of X-Tenant-Id.
const NO_TENANT = { skipTenantHeader: true } as const;

let nextMockId = 7891234569999;
let nextMockVersionId = 9999;

function paginate<T>(items: T[], page: number, pageSize: number) {
  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const start = (page - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  return {
    items: pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    hasPreviousPage: page > 1,
    hasNextPage: page < totalPages,
    firstItemOnPage: totalCount === 0 ? 0 : start + 1,
    lastItemOnPage: Math.min(start + pageSize, totalCount),
  };
}

export const notesService = {
  create: async (payload: NoteFormValues, signal?: AbortSignal): Promise<Note> => {
    if (USE_MOCK) {
      const now = new Date().toISOString();
      const note: Note = {
        id: String(nextMockId++),
        title: payload.title ?? null,
        content: payload.content,
        createdAt: now,
        updatedAt: now,
        versionCount: 0,
      };
      notesMock.unshift(note);
      return note;
    }
    const { data } = await httpClient.post<NoteFormValues, { id: string }>(
      `${API_V1}/notes`,
      payload,
      NO_TENANT,
    );
    return notesService.getById(data.id, signal);
  },

  getList: async (filters: NoteFilters = {}, signal?: AbortSignal): Promise<NotePagedResult> => {
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 10;

    if (USE_MOCK) {
      let items = [...notesMock];
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        items = items.filter(
          (n) => n.title?.toLowerCase().includes(term) || n.content?.toLowerCase().includes(term),
        );
      }
      items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      const result = paginate(items, page, pageSize);
      return {
        ...result,
        items: result.items.map(({ id, title, content, createdAt, updatedAt }) => ({
          id,
          title,
          preview: (content ?? '')
            .replace(/<[^>]+>/g, ' ')
            .trim()
            .slice(0, 150),
          createdAt,
          updatedAt,
        })),
      };
    }

    const { data } = await httpClient.get<NotePagedResult>(`${API_V1}/notes`, {
      ...NO_TENANT,
      params: toPascalCaseParams({ page, pageSize, searchTerm: filters.searchTerm }),
      signal,
    });
    return data;
  },

  getById: async (noteId: string, signal?: AbortSignal): Promise<Note> => {
    if (USE_MOCK) {
      const note = notesMock.find((n) => n.id === noteId);
      if (!note) throw new Error('Note.NotFound');
      return note;
    }
    const { data } = await httpClient.get<Note>(`${API_V1}/notes/${noteId}`, {
      ...NO_TENANT,
      signal,
    });
    return data;
  },

  update: async (noteId: string, payload: NoteFormValues): Promise<void> => {
    if (USE_MOCK) {
      const note = notesMock.find((n) => n.id === noteId);
      if (!note) throw new Error('Note.NotFound');

      const versions = noteVersionsMock[noteId] ?? [];
      const nextVersionNumber = versions.length > 0 ? versions[0].versionNumber + 1 : 1;
      const snapshot: NoteVersion = {
        id: String(nextMockVersionId++),
        versionNumber: nextVersionNumber,
        title: note.title,
        content: note.content,
        createdAt: new Date().toISOString(),
      };
      noteVersionsMock[noteId] = [snapshot, ...versions];
      noteVersionSummariesMock[noteId] = noteVersionsMock[noteId].map(
        ({ id, versionNumber, title, createdAt }) => ({ id, versionNumber, title, createdAt }),
      );

      note.title = payload.title ?? null;
      note.content = payload.content;
      note.updatedAt = new Date().toISOString();
      note.versionCount = noteVersionsMock[noteId].length;
      return;
    }
    await httpClient.put<NoteFormValues, void>(`${API_V1}/notes/${noteId}`, payload, NO_TENANT);
  },

  delete: async (noteId: string): Promise<void> => {
    if (USE_MOCK) {
      const idx = notesMock.findIndex((n) => n.id === noteId);
      if (idx !== -1) notesMock.splice(idx, 1);
      delete noteVersionsMock[noteId];
      delete noteVersionSummariesMock[noteId];
      return;
    }
    await httpClient.delete<void>(`${API_V1}/notes/${noteId}`, undefined, NO_TENANT);
  },

  getHistory: async (
    noteId: string,
    page = 1,
    pageSize = 20,
    signal?: AbortSignal,
  ): Promise<NoteVersionSummaryPagedResult> => {
    if (USE_MOCK) {
      const items = noteVersionSummariesMock[noteId] ?? [];
      return paginate(items, page, pageSize);
    }
    const { data } = await httpClient.get<NoteVersionSummaryPagedResult>(
      `${API_V1}/notes/${noteId}/history`,
      { ...NO_TENANT, params: toPascalCaseParams({ page, pageSize }), signal },
    );
    return data;
  },

  getVersion: async (
    noteId: string,
    versionId: string,
    signal?: AbortSignal,
  ): Promise<NoteVersion> => {
    if (USE_MOCK) {
      const version = (noteVersionsMock[noteId] ?? []).find((v) => v.id === versionId);
      if (!version) throw new Error('Note.VersionNotFound');
      return version;
    }
    const { data } = await httpClient.get<NoteVersion>(
      `${API_V1}/notes/${noteId}/history/${versionId}`,
      { ...NO_TENANT, signal },
    );
    return data;
  },

  restoreVersion: async (noteId: string, versionId: string): Promise<void> => {
    if (USE_MOCK) {
      const note = notesMock.find((n) => n.id === noteId);
      const version = (noteVersionsMock[noteId] ?? []).find((v) => v.id === versionId);
      if (!note || !version) throw new Error('Note.VersionNotFound');
      await notesService.update(noteId, { title: note.title, content: note.content ?? '' });
      note.title = version.title;
      note.content = version.content;
      note.updatedAt = new Date().toISOString();
      return;
    }
    await httpClient.post<undefined, void>(
      `${API_V1}/notes/${noteId}/history/${versionId}/restore`,
      undefined,
      NO_TENANT,
    );
  },

  exportNote: async (noteId: string, format: NoteExportFormat = 'plaintext'): Promise<void> => {
    const extension = format === 'markdown' ? 'md' : 'txt';
    const filename = `note-${noteId}.${extension}`;

    let blob: Blob;
    if (USE_MOCK) {
      const note = notesMock.find((n) => n.id === noteId);
      const text = (note?.content ?? '').replace(/<[^>]+>/g, '');
      blob = new Blob([text], { type: format === 'markdown' ? 'text/markdown' : 'text/plain' });
    } else {
      const { data } = await httpClient.get<Blob>(`${API_V1}/notes/${noteId}/export`, {
        ...NO_TENANT,
        params: { format },
        responseType: 'blob',
      });
      blob = data;
    }

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  },

  convertToTask: async (
    noteId: string,
    payload: ConvertToTaskRequest,
  ): Promise<ConvertToTaskResponse> => {
    if (USE_MOCK) {
      return { id: String(nextMockId++) };
    }
    // No NO_TENANT here: this creates a task in a tenant's project, so the API requires the
    // tenant header and runs its membership gate. The rest of the note calls stay user-scoped.
    const { data } = await httpClient.post<ConvertToTaskRequest, ConvertToTaskResponse>(
      `${API_V1}/notes/${noteId}/convert-to-task`,
      payload,
    );
    return data;
  },

  interpretNoteAsTask: async (
    noteId: string,
    payload: InterpretNoteAsTaskRequest,
  ): Promise<NoteTaskProposal> => {
    if (USE_MOCK) {
      return {
        title: 'Mock interpreted task',
        description: 'This is a mock interpretation',
        projectName: null,
        statusName: null,
        groupName: null,
        assigneeName: null,
        assigneeIsCaller: null,
        priority: null,
        startDateExpression: null,
        startDate: null,
        dueDateExpression: null,
        dueDate: null,
        estimatedMinutes: null,
        observations: [],
      };
    }
    // No NO_TENANT here either: the model call is billed per tenant, and a usage record with
    // tenant_id = 0 cannot be charged to anyone.
    const { data } = await httpClient.post<InterpretNoteAsTaskRequest, NoteTaskProposal>(
      `${API_V1}/notes/${noteId}/interpret-task`,
      payload,
    );
    return data;
  },
};
